import { useEffect, type RefObject } from 'react';

import {
  SCORE_COLUMN_WIDTH_VAR,
  clampScoreZoom,
  columnWidthAfterZoom,
  pinchFollowTranslate,
  scaleFromPinch,
  scaleFromWheel,
  scrollDeltaToKeepPoint,
  unscaledPointUnderFocal,
} from '../utils/scoreZoom';

const IOS_GESTURE_EVENTS = [
  'gesturestart',
  'gesturechange',
  'gestureend',
] as const;

const touchDistance = (a: Touch, b: Touch): number =>
  Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);

const touchMidpoint = (a: Touch, b: Touch): { x: number; y: number } => ({
  x: (a.clientX + b.clientX) / 2,
  y: (a.clientY + b.clientY) / 2,
});

const preventNativeGesture = (event: Event) => {
  event.preventDefault();
};

/**
 * Pinch (touch) and ctrl/cmd + wheel zoom that apply only to the white
 * score column. Chrome outside this subtree (toolbar, guest banner,
 * side/bottom toggles) keeps native browser zoom.
 *
 * Scale is a CSS transform so the score's max-width cap and the PDF overlay
 * ResizeObserver stay at the unzoomed layout size. While there is grey
 * margin left, the column itself grows (up to 90vw). Past that, X pans
 * inside the column and Y pans the page.
 *
 * During a pinch the score follows the fingers with translate+scale
 * only. Layout, column width, and scroll commit when the gesture ends
 * — iPhone Safari shakes if we scrollTo or grow the sizer on every
 * touchmove (visualViewport / URL-bar fight).
 */
export const useScoreZoom = ({
  viewportRef,
  sizerRef,
  contentRef,
}: {
  viewportRef: RefObject<HTMLElement | null>;
  sizerRef: RefObject<HTMLElement | null>;
  contentRef: RefObject<HTMLElement | null>;
}): void => {
  useEffect(() => {
    const viewport = viewportRef.current;
    const sizer = sizerRef.current;
    const content = contentRef.current;
    if (!viewport || !sizer || !content) return;

    let scale = 1;
    let baseWidth = viewport.clientWidth;
    let frozenBoxWidth = 0;
    let lastFocalX = 0;
    let lastFocalY = 0;
    let wheelBoxTimer = 0;

    const pinch = {
      active: false,
      startDist: 0,
      startScale: 1,
      originX: 0,
      originY: 0,
      layoutLeft: 0,
      layoutTop: 0,
    };
    const pan = {
      active: false,
      x: 0,
      y: 0,
      scrollLeft: 0,
      scrollY: 0,
    };

    const captureUnscaledSize = () => {
      baseWidth = viewport.clientWidth;
    };

    // `offsetHeight` ignores the zoom transform, so it is the unscaled
    // height even while zoomed. Read it fresh: labels and system spacing
    // can change the score's height at any zoom.
    const fitSizerHeight = (nextScale: number) => {
      sizer.style.height = `${content.offsetHeight * nextScale}px`;
    };

    const publishColumnWidth = () => {
      document.documentElement.style.setProperty(
        SCORE_COLUMN_WIDTH_VAR,
        `${viewport.getBoundingClientRect().width}px`
      );
    };

    const applyLayout = (nextScale: number, boxW: number) => {
      if (nextScale === 1) {
        content.style.transform = '';
        content.style.width = '';
        sizer.style.width = '';
        sizer.style.height = '';
        viewport.style.width = '';
        viewport.style.maxWidth = '';
        return;
      }

      const visualW = baseWidth * nextScale;
      viewport.style.width = `${boxW}px`;
      viewport.style.maxWidth = `${boxW}px`;
      content.style.transformOrigin = 'top left';
      content.style.transform = `scale(${nextScale})`;
      content.style.width = `${baseWidth}px`;
      sizer.style.width = `${visualW}px`;
      fitSizerHeight(nextScale);
    };

    const targetBoxWidth = (nextScale: number) =>
      columnWidthAfterZoom({
        baseWidth,
        scale: nextScale,
        viewportWidth: window.innerWidth,
      });

    const keepPointUnderFocal = (
      unscaledX: number,
      unscaledY: number,
      focalX: number,
      focalY: number
    ) => {
      const next = content.getBoundingClientRect();
      const delta = scrollDeltaToKeepPoint({
        visualLeft: next.left,
        visualTop: next.top,
        unscaledX,
        unscaledY,
        scale,
        focalX,
        focalY,
      });
      viewport.scrollLeft = Math.max(0, viewport.scrollLeft + delta.x);
      window.scrollTo(window.scrollX, Math.max(0, window.scrollY + delta.y));
    };

    const applyScale = (
      nextScale: number,
      focalX: number,
      focalY: number,
      boxW: number
    ) => {
      const clamped = clampScoreZoom(nextScale);
      lastFocalX = focalX;
      lastFocalY = focalY;
      if (clamped === scale) return;

      if (scale === 1) captureUnscaledSize();

      const rect = content.getBoundingClientRect();
      const unscaled = unscaledPointUnderFocal({
        visualLeft: rect.left,
        visualTop: rect.top,
        focalX,
        focalY,
        scale,
      });

      scale = clamped;
      applyLayout(clamped, boxW);
      if (clamped === 1) captureUnscaledSize();
      keepPointUnderFocal(unscaled.x, unscaled.y, focalX, focalY);
    };

    const settleBoxWidth = () => {
      frozenBoxWidth = 0;
      const rect = content.getBoundingClientRect();
      const unscaled = unscaledPointUnderFocal({
        visualLeft: rect.left,
        visualTop: rect.top,
        focalX: lastFocalX,
        focalY: lastFocalY,
        scale,
      });
      applyLayout(scale, targetBoxWidth(scale));
      keepPointUnderFocal(unscaled.x, unscaled.y, lastFocalX, lastFocalY);
      publishColumnWidth();
    };

    const settlePinch = () => {
      applyLayout(scale, targetBoxWidth(scale));
      if (scale !== 1) {
        keepPointUnderFocal(
          pinch.originX,
          pinch.originY,
          lastFocalX,
          lastFocalY
        );
      }
      publishColumnWidth();
    };

    const applyPinchVisual = (
      nextScale: number,
      midX: number,
      midY: number
    ) => {
      const clamped = clampScoreZoom(nextScale);
      lastFocalX = midX;
      lastFocalY = midY;

      if (scale === 1 && clamped !== 1) {
        captureUnscaledSize();
        content.style.width = `${baseWidth}px`;
      }

      scale = clamped;
      if (clamped === 1) captureUnscaledSize();

      const { x, y } = pinchFollowTranslate({
        midX,
        midY,
        originX: pinch.originX,
        originY: pinch.originY,
        scale: clamped,
        layoutLeft: pinch.layoutLeft,
        layoutTop: pinch.layoutTop,
      });
      content.style.transformOrigin = 'top left';
      content.style.transform = `translate(${x}px, ${y}px) scale(${clamped})`;
    };

    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      if (!frozenBoxWidth) {
        frozenBoxWidth = viewport.getBoundingClientRect().width;
      }
      applyScale(
        scaleFromWheel(scale, event.deltaY),
        event.clientX,
        event.clientY,
        frozenBoxWidth
      );
      window.clearTimeout(wheelBoxTimer);
      wheelBoxTimer = window.setTimeout(settleBoxWidth, 80);
    };

    const onPinchStart = (event: TouchEvent) => {
      if (event.touches.length !== 2) return;
      event.preventDefault();
      pan.active = false;
      pinch.active = true;
      pinch.startDist = touchDistance(event.touches[0], event.touches[1]);
      pinch.startScale = scale;

      if (scale === 1) {
        captureUnscaledSize();
        content.style.width = `${baseWidth}px`;
      }

      const mid = touchMidpoint(event.touches[0], event.touches[1]);
      lastFocalX = mid.x;
      lastFocalY = mid.y;
      const rect = content.getBoundingClientRect();
      const origin = unscaledPointUnderFocal({
        visualLeft: rect.left,
        visualTop: rect.top,
        focalX: mid.x,
        focalY: mid.y,
        scale,
      });
      pinch.originX = origin.x;
      pinch.originY = origin.y;
      pinch.layoutLeft = rect.left;
      pinch.layoutTop = rect.top;

      content.style.willChange = 'transform';
      viewport.style.touchAction = 'none';
    };

    const onPinchMove = (event: TouchEvent) => {
      if (!pinch.active || event.touches.length !== 2) return;
      event.preventDefault();
      const mid = touchMidpoint(event.touches[0], event.touches[1]);
      applyPinchVisual(
        scaleFromPinch(
          pinch.startScale,
          pinch.startDist,
          touchDistance(event.touches[0], event.touches[1])
        ),
        mid.x,
        mid.y
      );
    };

    const onPinchEnd = (event: TouchEvent) => {
      if (event.touches.length >= 2) return;
      if (!pinch.active) return;
      pinch.active = false;
      content.style.willChange = '';
      viewport.style.touchAction = '';
      settlePinch();
    };

    const onPanStart = (event: TouchEvent) => {
      if (scale === 1 || pinch.active || event.touches.length !== 1) return;
      const t = event.touches[0];
      pan.active = true;
      pan.x = t.clientX;
      pan.y = t.clientY;
      pan.scrollLeft = viewport.scrollLeft;
      pan.scrollY = window.scrollY;
    };

    const onPanMove = (event: TouchEvent) => {
      if (!pan.active || event.touches.length !== 1) return;
      event.preventDefault();
      const t = event.touches[0];
      viewport.scrollLeft = Math.max(0, pan.scrollLeft - (t.clientX - pan.x));
      window.scrollTo(
        window.scrollX,
        Math.max(0, pan.scrollY - (t.clientY - pan.y))
      );
    };

    const onPanEnd = () => {
      pan.active = false;
    };

    const onWindowResize = () => {
      if (scale === 1) {
        captureUnscaledSize();
        return;
      }
      applyLayout(scale, targetBoxWidth(scale));
    };

    const resizeObserver = new ResizeObserver((entries) => {
      if (pinch.active) return;
      for (const entry of entries) {
        if (entry.target === content && scale !== 1) fitSizerHeight(scale);
        if (entry.target === viewport) publishColumnWidth();
      }
    });
    resizeObserver.observe(content);
    resizeObserver.observe(viewport);
    publishColumnWidth();
    window.addEventListener('resize', onWindowResize);

    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('touchstart', onPinchStart, {
      passive: false,
      capture: true,
    });
    viewport.addEventListener('touchmove', onPinchMove, {
      passive: false,
      capture: true,
    });
    viewport.addEventListener('touchend', onPinchEnd, { capture: true });
    viewport.addEventListener('touchcancel', onPinchEnd, { capture: true });
    for (const type of IOS_GESTURE_EVENTS) {
      viewport.addEventListener(type, preventNativeGesture);
    }
    viewport.addEventListener('touchstart', onPanStart);
    viewport.addEventListener('touchmove', onPanMove, { passive: false });
    viewport.addEventListener('touchend', onPanEnd);
    viewport.addEventListener('touchcancel', onPanEnd);

    return () => {
      resizeObserver.disconnect();
      window.clearTimeout(wheelBoxTimer);
      document.documentElement.style.removeProperty(SCORE_COLUMN_WIDTH_VAR);
      content.style.willChange = '';
      viewport.style.touchAction = '';
      window.removeEventListener('resize', onWindowResize);
      viewport.removeEventListener('wheel', onWheel);
      viewport.removeEventListener('touchstart', onPinchStart, true);
      viewport.removeEventListener('touchmove', onPinchMove, true);
      viewport.removeEventListener('touchend', onPinchEnd, true);
      viewport.removeEventListener('touchcancel', onPinchEnd, true);
      for (const type of IOS_GESTURE_EVENTS) {
        viewport.removeEventListener(type, preventNativeGesture);
      }
      viewport.removeEventListener('touchstart', onPanStart);
      viewport.removeEventListener('touchmove', onPanMove);
      viewport.removeEventListener('touchend', onPanEnd);
      viewport.removeEventListener('touchcancel', onPanEnd);
    };
  }, [viewportRef, sizerRef, contentRef]);
};
